import type { ColumnConfiguration, ListBuilderConfiguration } from '../types';

/**
 * Lot column for the invoice row expansion (PD-15838). Same rationale as
 * INVOICE_DETAIL_LOT_COLUMN: addable-but-off for everyone, on by default for the
 * Blue Link tenants through the apps/config-api migration that imports this.
 */
export const INVOICE_EXPANSION_LINES_LOT_COLUMN: ColumnConfiguration = {
	id: 'lot',
	headers: [{ id: 'lot-header', text: 'Lot' }],
	fields: [
		{
			id: 'lot-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lotNumber',
					objectTable: 'invoice_detail',
				},
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'lotNumber',
			objectTable: 'invoice_detail',
		},
	],
	alignment: 'left',
	minWidth: 120,
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
};

/**
 * Invoice line items shown inside the invoice list's row expansion
 * (PD-15221).
 *
 * Rendered as an embedded list when an invoice row is expanded on the main
 * Invoices page. Data comes from GET /invoices/:id/details via the module's
 * rowExpansionQuery callback (which normalizes itemId / itemNumber) — rows
 * exist only for the expanded invoice; this is NOT a standalone page list.
 *
 * Own listKey (not `invoice_detail`) so customizing the expansion can never
 * restyle the Invoice Details page's line-items card.
 *
 * Invoices are money documents, so the defaults lean financial: item, item
 * number, quantity, price, and line total. The invoice details endpoint
 * carries no stock information, so there is no Qty On Hand here.
 */
export const INVOICE_EXPANSION_LINES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-invoice-expansion-lines',
			cacheTTL: 600000,
			name: 'Invoice Line Items',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'line-number-asc',
			columns: [
				{
					id: 'item',
					headers: [{ id: 'item-header', text: 'Item' }],
					fields: [
						{
							id: 'item-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemName',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'link' },
								fontWeight: 'semibold',
							},
							link: {
								enabled: true,
								type: 'object',
								objectTable: 'item',
								idField: 'itemId',
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'itemName',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'left',
					minWidth: 250,
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'middle',
				},
				{
					id: 'item-number',
					headers: [{ id: 'item-number-header', text: 'Item #' }],
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemNumber' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [{ direction: 'ASC', fieldName: 'itemNumber' }],
					alignment: 'left',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantity',
					headers: [{ id: 'quantity-header', text: 'Qty' }],
					fields: [
						{
							id: 'quantity-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantity',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantity',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 80,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'price',
					headers: [{ id: 'price-header', text: 'Price' }],
					fields: [
						{
							id: 'price-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'price',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'price',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'total',
					headers: [{ id: 'total-header', text: 'Total' }],
					fields: [
						{
							id: 'total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'total',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'total',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
			],
			description: 'Line items shown when an invoice row is expanded',
			requiredFields: [
				'invoice_detail._id',
				'invoice_detail._fk_item__id',
				'invoice_detail.lineNumber',
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [],
		},
		{
			baseUrl: '/invoices/:invoiceId',
			objectLabel: 'Invoice Line Item',
			objectTable: 'invoice_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lineNumber', fieldLabel: 'Line Item' },
				{ fieldName: 'itemNumber', fieldLabel: 'Item #' },
				{ fieldName: 'itemName', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'lotNumber', fieldLabel: 'Lot' },
				{ fieldName: 'quantity', fieldLabel: 'Qty' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
				{ fieldName: 'total', fieldLabel: 'Total' },
			],
		},
	],
	tableSorts: [
		{
			id: 'line-number-asc',
			name: 'Line Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lineNumber',
					objectTable: 'invoice_detail',
				},
			],
			description: 'Sort by line number',
		},
		{
			id: 'description-asc',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'itemName',
					objectTable: 'invoice_detail',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'total-desc',
			name: 'Total',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'total',
					objectTable: 'invoice_detail',
				},
			],
			description: 'Sort by line total (highest first)',
		},
	],
};
